<script setup lang="ts">
import { useAppSettingsStore } from '@convatec/180medical-website/app/stores/app-settings'
import { ScrollToFormDisplayType } from '~~/types/enums'

const { model } = defineProps(['model'])
const appSettingsStore = useAppSettingsStore()
const { showFooterForm, showSidebarForm, showSidebarContactInfo } = useLayoutFlags()

const showSidebarNav = computed(
  () => model.menuSettingsBlock?.showSidebarNav?.value ?? false,
)

const contactForm = resolveContactFormPlacement({
  sidebarContactForm: model.showSidebarContactForm?.value ?? false,
  sidebarNav: showSidebarNav.value,
  footerFormAllowed: model.scrollToFormDisplay?.value !== ScrollToFormDisplayType.SuppressFormAndBlock,
})

showFooterForm.value = contactForm.showFooterForm
showSidebarForm.value = contactForm.showSidebarForm
showSidebarContactInfo.value = model.showSidebarContactInfo?.value || false

const showScrollToFormDisplay = computed(
  () =>
    model.scrollToFormDisplay?.value === ScrollToFormDisplayType.ShowFormAndBlock
    && !!appSettingsStore.contactSettings.scrollToTitle,
)

const showSidebar = computed(() => {
  // nav takes precedence
  if (showSidebarNav.value) {
    return false
  }

  return (
    showScrollToFormDisplay.value
    || showSidebarForm.value
    || showSidebarContactInfo.value
  )
})

const sidebarWidth = computed<'narrow' | 'wide'>(() => {
  if (showSidebarForm.value || showSidebarContactInfo.value) {
    return 'wide'
  }

  return 'narrow'
})

const mainBodyWidth = computed<'narrow' | 'wide'>(() => {
  return showSidebar.value ? 'wide' : 'narrow'
})

const heroShowsTitle = computed(() => Boolean(model.pageHeroContent?.heroStyle?.value && model.pageHeroContent?.heroTitle?.value))
</script>

<template>
  <div class="general-page">
    <template v-if="model.pageHeroContent?.heroStyle?.value">
      <HeroBlock
        :variation="model.pageHeroContent?.heroStyle?.value"
        :title="model.pageHeroContent?.heroTitle?.value"
        :description="model.pageHeroContent?.heroDescription?.value"
        :color="model.pageHeroContent?.heroColor?.value"
        :image="model.pageHeroContent?.aprimoHeroImage?.url || '/images/hero-basic.jpg'"
        :image-alt-text="model.pageHeroContent?.heroImageAltText?.value || 'Hero Image'"
        :image-width="model.pageHeroContent?.aprimoHeroImage?.width"
        :image-height="model.pageHeroContent?.aprimoHeroImage?.height"
        :body="model.pageHeroContent?.heroBody?.value"
      />
    </template>
    <div class="container-fluid">
      <div class="general-page__wrap">
        <LazyLayoutSidebarNav
          v-if="model.menuSettingsBlock?.showSidebarNav?.value"
          :id="model.contentLink?.id"
        />
        <div
          class="main-body"
          :class="`main-body--${mainBodyWidth}`"
        >
          <div
            v-if="model.pageTitle?.value && !heroShowsTitle"
            class="general-page__title prose"
          >
            <h1>
              {{ model.pageTitle.value }}
            </h1>
          </div>
          <div class="general-page__body">
            <div class="body-block-area w-full">
              <template v-if="model.pageBody?.value">
                <EditorialBlock>
                  <div v-html="convertH1ToH2(stripEmptyHeadings(model.pageBody.value))" />
                </EditorialBlock>
              </template>
              <GenericsContentArea
                v-if="model.bodyBlockArea?.expandedValue"
                :model="model.bodyBlockArea.expandedValue"
              />
            </div>
            <aside
              v-if="showSidebar"
              class="sidebar"
              :class="`sidebar--${sidebarWidth}`"
            >
              <SideFormBlock
                v-if="showSidebarForm"
                title="Contact Us"
              >
                <div id="hs-sb-contact-form-container" />
              </SideFormBlock>

              <SideContactInfoBlock
                v-if="showSidebarContactInfo"
                :contact-info="appSettingsStore.contactInfo"
              />

              <ScrollToFormBlock
                v-if="showScrollToFormDisplay"
                :title="appSettingsStore?.contactSettings?.scrollToTitle"
                :body="appSettingsStore?.contactSettings?.scrollToBody"
                :text="appSettingsStore?.contactSettings?.scrollToText"
              />
            </aside>
          </div>
        </div>
      </div>
      <div class="page-block-area flex flex-col items-center w-full">
        <GenericsContentArea
          v-if="model.pageBlockArea?.expandedValue"
          :model="model.pageBlockArea.expandedValue"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  @use '@/assets/css/forms/hubspot-contact-form.scss';
@use '@/assets/css/pages/general-page.scss';
</style>
